import { useCallback, useEffect, useState } from "react"
import { useNavigate } from "react-router-dom"
import { Link } from "react-router-dom"
import {
  AlertCircle,
  Building2,
  Coins,
  DollarSign,
  Globe,
  Loader2,
  RefreshCw,
  TrendingUp,
} from "lucide-react"
import { Button } from "@/components/ui/button"
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/components/ui/card"
import { supabase } from "@/lib/supabase"
import { formatCurrency } from "@/lib/format"
import { ADMIN_EMAIL } from "@/lib/constants"
import {
  countsTowardFeeThreshold,
  currentYearStartUtcIso,
  PLATFORM_FEE_THRESHOLD_CENTS,
} from "@/lib/feeThreshold"
import { toast } from "sonner"
import { useAuthStore } from "@/stores/authStore"

type AdminMetrics = {
  totalOrgs: number
  orgsReceivingDonations: number
  totalProcessedCents: number
  totalFeesCents: number
  orgsWithin20PercentOfThreshold: number
}

function formatCount(n: number): string {
  return n.toLocaleString("en-US", { maximumFractionDigits: 0 })
}

export function AdminDashboard() {
  const { user } = useAuthStore()
  const navigate = useNavigate()
  const [metrics, setMetrics] = useState<AdminMetrics | null>(null)
  const [loading, setLoading] = useState(true)
  const [refreshing, setRefreshing] = useState(false)

  const fetchMetrics = useCallback(async () => {
    try {
      const [orgsRes, donationsRes] = await Promise.all([
        supabase.from("organizations").select("*", { count: "exact", head: true }),
        supabase
          .from("donations")
          .select("org_id, amount, platform_fee, status, source, created_at")
          .eq("status", "succeeded"),
      ])

      if (orgsRes.error) throw orgsRes.error
      if (donationsRes.error) throw donationsRes.error

      const totalOrgs = orgsRes.count ?? 0
      const donations = donationsRes.data ?? []

      const orgIdsWithDonations = new Set(donations.map((d) => d.org_id))
      const orgsReceivingDonations = orgIdsWithDonations.size

      // Platform-wide reporting stays unscoped (all-time, all sources); the
      // fee-threshold metric below uses the exact fee-counter definition
      // (NOS-205): current calendar year (UTC) + adopted renewals excluded.
      const yearStartIso = currentYearStartUtcIso()
      let totalProcessedCents = 0
      let totalFeesCents = 0
      const feeCounterTotalsByOrg = new Map<string, number>()

      for (const d of donations) {
        totalProcessedCents += d.amount
        if (d.platform_fee != null && d.platform_fee > 0) {
          totalFeesCents += d.platform_fee
        }
        if (countsTowardFeeThreshold(d, yearStartIso)) {
          feeCounterTotalsByOrg.set(
            d.org_id,
            (feeCounterTotalsByOrg.get(d.org_id) ?? 0) + d.amount
          )
        }
      }

      const LOW_CENTS = 800_000
      const HIGH_CENTS = PLATFORM_FEE_THRESHOLD_CENTS
      let orgsWithin20PercentOfThreshold = 0
      for (const total of feeCounterTotalsByOrg.values()) {
        if (total >= LOW_CENTS && total <= HIGH_CENTS) {
          orgsWithin20PercentOfThreshold += 1
        }
      }

      setMetrics({
        totalOrgs,
        orgsReceivingDonations,
        totalProcessedCents,
        totalFeesCents,
        orgsWithin20PercentOfThreshold,
      })
    } catch (err) {
      console.error("Admin metrics fetch error:", err)
      toast.error("Failed to load admin metrics. Please try again.")
    } finally {
      setLoading(false)
      setRefreshing(false)
    }
  }, [])

  useEffect(() => {
    const email = user?.email ?? null
    if (email === null) return
    if (email !== ADMIN_EMAIL) {
      toast.error("Unauthorized access")
      navigate("/dashboard", { replace: true })
      return
    }
    fetchMetrics()
  }, [user?.email, navigate, fetchMetrics])

  const handleRefresh = () => {
    setRefreshing(true)
    fetchMetrics()
  }

  const email = user?.email ?? null
  if (email !== null && email !== ADMIN_EMAIL) {
    return null
  }

  if (loading && !metrics) {
    return (
      <div className="flex min-h-[40vh] flex-col items-center justify-center gap-4">
        <Loader2 className="h-10 w-10 animate-spin text-primary" aria-hidden />
        <p className="text-muted-foreground">Loading platform metrics...</p>
      </div>
    )
  }

  return (
    <div className="bg-background">
      <section className="mb-8">
        <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
          <h2 className="text-lg font-semibold">Platform metrics</h2>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" asChild>
              <Link to="/admin/subdomains">
                <Globe className="mr-2 h-4 w-4" />
                Subdomain management
              </Link>
            </Button>
            <Button
            variant="outline"
            size="sm"
            onClick={handleRefresh}
            disabled={refreshing}
          >
            {refreshing ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Refreshing...
              </>
            ) : (
              <>
                <RefreshCw className="mr-2 h-4 w-4" />
                Refresh
              </>
            )}
          </Button>
          </div>
        </div>
        <h2 className="sr-only">Admin stats</h2>
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Total Num of Orgs</CardTitle>
              <Building2 className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <p className="text-2xl font-bold">{metrics ? formatCount(metrics.totalOrgs) : "—"}</p>
              <p className="text-xs text-muted-foreground">All organizations</p>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">
                Orgs Receiving Donations
              </CardTitle>
              <TrendingUp className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <p className="text-2xl font-bold">
                {metrics ? formatCount(metrics.orgsReceivingDonations) : "—"}
              </p>
              <p className="text-xs text-muted-foreground">
                With at least one succeeded donation
              </p>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Total $ Processed</CardTitle>
              <DollarSign className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <p className="text-2xl font-bold">
                {metrics ? formatCurrency(metrics.totalProcessedCents) : "—"}
              </p>
              <p className="text-xs text-muted-foreground">Succeeded donations only</p>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Total Fees Collected</CardTitle>
              <Coins className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <p className="text-2xl font-bold">
                {metrics ? formatCurrency(metrics.totalFeesCents) : "—"}
              </p>
              <p className="text-xs text-muted-foreground">
                Platform fees from succeeded donations
              </p>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">
                Orgs within 20% of Fee Threshold
              </CardTitle>
              <AlertCircle className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <p className="text-2xl font-bold">
                {metrics
                  ? formatCount(metrics.orgsWithin20PercentOfThreshold)
                  : "—"}
              </p>
              <p className="text-xs text-muted-foreground">
                $8,000–$10,000 NOS-processed this calendar year (UTC) — excludes
                adopted renewals
              </p>
            </CardContent>
          </Card>
          <Card className="flex flex-col items-center justify-center">
            <Button
              variant="outline"
              size="sm"
              onClick={handleRefresh}
              disabled={refreshing}
            >
              {refreshing ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <>
                  <RefreshCw className="mr-2 h-4 w-4" />
                  Refresh
                </>
              )}
            </Button>
          </Card>
        </div>
      </section>
      <SponsorBillingAdminSection />
    </div>
  )
}

interface SponsorOrgAdminRow {
  org_id: string
  name: string
  sponsor_subscription_status: string
  is_founding_sponsor: boolean
  sponsor_manual_active: boolean
  child_count: number
}

/**
 * NOS-170: manual-invoice escape hatch — mark a sponsor org active without
 * Stripe (early deals). Platform admin only (enforced again server-side).
 * NOS-196: enable-by-slug input reaches orgs with no sponsor signal yet (a
 * fresh org is not in the list by design); the list itself is widened
 * server-side to any org with children, status ≠ none, or the manual flag.
 */
function SponsorBillingAdminSection() {
  const [rows, setRows] = useState<SponsorOrgAdminRow[]>([])
  const [loaded, setLoaded] = useState(false)
  const [slug, setSlug] = useState("")
  const [enabling, setEnabling] = useState(false)

  const fetchRows = useCallback(async () => {
    const { data, error } = await supabase.rpc("list_sponsor_orgs_admin")
    if (!error && data) setRows(data as SponsorOrgAdminRow[])
    setLoaded(true)
  }, [])

  useEffect(() => {
    fetchRows()
  }, [fetchRows])

  const toggleManual = async (row: SponsorOrgAdminRow) => {
    const { error } = await supabase.rpc("set_sponsor_manual_active", {
      org_uuid: row.org_id,
      active: !row.sponsor_manual_active,
    })
    if (error) {
      toast.error(error.message)
      return
    }
    toast.success(
      `${row.name}: manual billing ${row.sponsor_manual_active ? "disabled" : "enabled"}`
    )
    fetchRows()
  }

  const enableBySlug = async (e: React.FormEvent) => {
    e.preventDefault()
    const trimmed = slug.trim()
    if (!trimmed) return
    setEnabling(true)
    const { data, error } = await supabase.rpc("enable_sponsor_manual_by_slug", {
      p_slug: trimmed,
    })
    setEnabling(false)
    if (error) {
      toast.error(error.message)
      return
    }
    const enabled = (data as { org_id: string; name: string }[] | null)?.[0]
    toast.success(`${enabled?.name ?? trimmed}: manual billing enabled`)
    setSlug("")
    fetchRows()
  }

  if (!loaded) return null

  return (
    <section className="mt-8">
      <h2 className="mb-4 text-lg font-semibold">Sponsor billing overrides</h2>
      <form onSubmit={enableBySlug} className="mb-4 flex flex-wrap items-center gap-2">
        <input
          type="text"
          value={slug}
          onChange={(e) => setSlug(e.target.value)}
          placeholder="org-slug"
          aria-label="Organization slug"
          className="h-9 w-56 rounded-md border border-input bg-white px-3 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
        />
        <Button type="submit" variant="outline" size="sm" disabled={enabling || !slug.trim()}>
          {enabling ? (
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
          ) : null}
          Enable manual billing by slug
        </Button>
        <p className="w-full text-xs text-gray-500 sm:w-auto">
          Enables the sponsor suite for any org without Stripe — white-glove deals and demos.
        </p>
      </form>
      {rows.length === 0 ? (
        <p className="rounded-lg border border-dashed bg-white px-4 py-6 text-sm text-gray-500">
          No sponsor organizations yet — orgs appear here once they have sponsored projects, a
          subscription, or manual billing enabled.
        </p>
      ) : (
      <div className="overflow-x-auto rounded-lg border bg-white">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              {["Sponsor org", "Projects", "Subscription", "Founding", "Manual billing"].map(
                (h) => (
                  <th
                    key={h}
                    className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wide text-gray-500"
                  >
                    {h}
                  </th>
                )
              )}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {rows.map((row) => (
              <tr key={row.org_id}>
                <td className="px-4 py-3 font-medium">{row.name}</td>
                <td className="px-4 py-3 tabular-nums">{row.child_count}</td>
                <td className="px-4 py-3">{row.sponsor_subscription_status}</td>
                <td className="px-4 py-3">{row.is_founding_sponsor ? "yes" : "—"}</td>
                <td className="px-4 py-3">
                  <Button variant="outline" size="sm" onClick={() => toggleManual(row)}>
                    {row.sponsor_manual_active ? "Disable" : "Enable"}
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      )}
    </section>
  )
}
